/**
 * MobX store for filler word removal state.
 */
import { makeAutoObservable, runInAction } from 'mobx';
import { 
    DEFAULT_ENABLED_FILLER_WORD_CATEGORY_IDS,
    FillerWordOccurrence,
    FILLER_WORD_CATEGORIES,
    REPEATED_WORD_CATEGORY_ID,
    findFillerMatchAt,
    detectRepeatedWords,
} from './fillerWord';
import { V2TranscriptWord } from '../V2TextEditor';
import { CompositionService } from '../../../librettoEditor/services/CompositionService';
import { ScriptLayerStore } from '../../../librettoEditor/store/ScriptLayerStore';
import { ProjectAssetsStore } from '../../../state/ProjectAssetsStore';
import { isScriptClip, ScriptLayerClip } from '../../../engine/clips';
import { buildScriptTranscriptWords } from '../../../librettoEditor/utils/scriptTranscriptWords';
import {computeFillerPaddingSec} from "./computeFillerPaddingSec";
import { focusTranscriptWordAtTime } from '../V2TextEditor/transcriptNavigation';
import {
    AIFillerWordsDetectionPayload,
    FillerWordTokenizedWord,
    PostAiFillerWordsFn,
    classifyAmbiguousFillerWords,
} from "../ai/fillerWords";
import {
    buildAmbiguousFillerWordRequests,
    buildLocalFillerWordCandidates,
    mergeFillerWordServerDecisions,
} from "./fillerWordPipeline";

// Type for the GetTranscript function from useAssetStore hook
type GetTranscriptFn = (params: { transcriptId: string; projectId: string }) => Promise<any | undefined>;

export type FillerWordCutMode = 'ignore' | 'delete' | 'gap' | 'transcript-only';

const createDefaultEnabledCategories = () => new Set<string>(DEFAULT_ENABLED_FILLER_WORD_CATEGORY_IDS);

class FillerWordStoreImpl {
    // Whether filler word removal mode is active
    isActive: boolean = false;
    
    // Current occurrences found
    occurrences: FillerWordOccurrence[] = [];
    
    // Currently focused occurrence index
    currentIndex: number = 0;
    
    // Which filler word categories are enabled
    enabledCategories: Set<string> = createDefaultEnabledCategories();
    
    // Avoid harsh cuts toggle (default OFF - show all)
    avoidHarshCuts: boolean = false;
    
    // Whether audio analysis is in progress
    isAnalyzing: boolean = false;
    
    // Whether initial transcript/LLM detection is in progress
    isInitializing: boolean = false;
    
    // Progress of audio analysis (0-100)
    analysisProgress: number = 0;
    
    // Whether removal is in progress
    isRemoving: boolean = false;
    
    // Progress of removal (0-100)
    removalProgress: number = 0;
    
    // Cut mode for removal
    cutMode: FillerWordCutMode = 'ignore';
    
    // Counts per category
    categoryCounts: Map<string, number> = new Map();
    
    // All transcript words (for reference)
    private allWords: V2TranscriptWord[] = [];
    // Raw LLM detections for current transcript snapshot.
    private aiDetections: AIFillerWordsDetectionPayload[] | null = null;
    private postAiFillerWords?: PostAiFillerWordsFn;
    private detectionRequestGeneration: number = 0;

    // Track whether current occurrences have been analyzed for harsh cuts.
    private scanGeneration: number = 0;
    private analyzedGeneration: number = -1;

    constructor() {
        makeAutoObservable(this, {}, { autoBind: true });
    }

    private logSmoothCuts(label: string, payload?: Record<string, any>) {
        if (payload == null) {
            console.log(`[FillerWord][smooth-cuts] ${label}`);
            return;
        }
        console.log(`[FillerWord][smooth-cuts] ${label} ${JSON.stringify(payload)}`);
    }

    get includeRepeatedWords(): boolean {
        return this.enabledCategories.has(REPEATED_WORD_CATEGORY_ID);
    }

    private buildWordTokenId(word: V2TranscriptWord, index: number): string {
        const sourceIndex = Number.isFinite(word?.sourceIndex) ? Number(word.sourceIndex) : 0;
        const srcStart = Number.isFinite(word?.srcStartSec)
            ? Number(word.srcStartSec)
            : Number(word?.startSec ?? 0);
        const srcEnd = Number.isFinite(word?.srcEndSec)
            ? Number(word.srcEndSec)
            : Number(word?.endSec ?? word?.startSec ?? 0);
        return `fw_${sourceIndex}_${srcStart.toFixed(6)}_${srcEnd.toFixed(6)}_${index}`;
    }

    /**
     * Activate filler word removal mode and scan for occurrences.
     */
    activate(words: V2TranscriptWord[]) {
        this.isActive = true;
        this.allWords = words;
        this.aiDetections = null;
        this.postAiFillerWords = undefined;
        this.enabledCategories = createDefaultEnabledCategories();
        this.cutMode = 'ignore';
        this.scanForFillerWords();
    }
    
    /**
     * Activate filler word removal mode by fetching words from the script clip.
     * This fetches transcript data from the backend.
     * @param getTranscript - The GetTranscript function from useAssetStore hook
     */
    async activateAsync(getTranscript: GetTranscriptFn, postAiFillerWords?: PostAiFillerWordsFn) {
        // Get the script layer info from CompositionStateStore
        const scriptLayerInfo = CompositionService.composition.scriptLayer;
        const scriptClipId = scriptLayerInfo?.clipId;

        if (!scriptClipId) {
            return;
        }

        const clip = CompositionService.getClipById(scriptClipId);
        if (!clip || !isScriptClip(clip)) {
            return;
        }
        
        runInAction(() => {
            this.isActive = true;
            this.isInitializing = true;
            this.occurrences = [];
            this.currentIndex = 0;
            this.enabledCategories = createDefaultEnabledCategories();
            this.cutMode = 'ignore';
            this.categoryCounts.clear();
            this.allWords = [];
            this.aiDetections = null;
            this.postAiFillerWords = postAiFillerWords;
        });
        
        const scriptClip = clip as ScriptLayerClip;
        const projectId = ProjectAssetsStore.projectId as string;
        
        try {
            const allWords: V2TranscriptWord[] = await buildScriptTranscriptWords({
                scriptClip,
                trackRecords: ProjectAssetsStore.projectTracks,
                cutFilterMode: 'playable',
                fetchTranscriptForTrack: async ({ transcriptId }) => await getTranscript({ transcriptId, projectId }),
            });

            // Add stable token ids for LLM round-trip mapping.
            const tokenizedWords: FillerWordTokenizedWord[] = allWords.map((word, index) => ({
                ...word,
                tokenId: this.buildWordTokenId(word, index),
            }));

            runInAction(() => {
                this.allWords = tokenizedWords;
            });
            await this.refreshDetections();
        } finally {
            runInAction(() => {
                this.isInitializing = false;
            });
        }
    }

    /**
     * Deactivate filler word removal mode.
     */
    deactivate() {
        this.isActive = false;
        this.occurrences = [];
        this.currentIndex = 0;
        this.categoryCounts.clear();
        this.aiDetections = null;
        this.postAiFillerWords = undefined;
        this.isInitializing = false;
        this.isAnalyzing = false;
        this.analysisProgress = 0;
        this.isRemoving = false;
        this.removalProgress = 0;
    }
    
    /**
     * Rescan filler words using the provided getTranscript function.
     * This is useful after removals to get fresh data based on current cuts.
     */
    async rescan(getTranscript: GetTranscriptFn, postAiFillerWords?: PostAiFillerWordsFn) {
        if (!this.isActive) return;

        // Reset state but keep settings
        const savedCategories = new Set(this.enabledCategories);
        const savedAvoidHarsh = this.avoidHarshCuts;
        const savedCutMode = this.cutMode;

        // Re-fetch and scan
        await this.activateAsync(getTranscript, postAiFillerWords);

        // Restore settings
        runInAction(() => {
            this.enabledCategories = savedCategories;
            this.avoidHarshCuts = savedAvoidHarsh;
            this.cutMode = savedCutMode;
        });
        await this.refreshDetections();
    }

    private maybeAnalyzeForSmoothCuts() {
        if (!this.isActive) return;
        if (this.isInitializing) return;
        if (!this.avoidHarshCuts) return;
        if (this.isAnalyzing) return;
        if (this.occurrences.length === 0) return;
        if (this.analyzedGeneration === this.scanGeneration) return;
        void this.analyzeAllOccurrences();
    }
    
    /**
     * Start tracking removal progress.
     */
    startRemoval() {
        this.isRemoving = true;
        this.removalProgress = 0;
    }
    
    /**
     * Update removal progress (0-100).
     */
    updateRemovalProgress(progress: number) {
        this.removalProgress = Math.round(progress);
    }
    
    /**
     * Finish removal progress.
     */
    finishRemoval() {
        this.isRemoving = false;
        this.removalProgress = 100;
    }
    
    /**
     * Run audio analysis on all occurrences to determine harshness using the scoring system.
     * Updates isHarshCut on each occurrence based on actual audio analysis.
     */
    async analyzeAllOccurrences() {
        if (this.occurrences.length === 0) {
            this.logSmoothCuts("skipped", { reason: "no_occurrences" });
            return;
        }

        // Get the script clip for audio analysis
        const scriptClip = CompositionService.getScriptLayerClip() as ScriptLayerClip | null;
        if (!scriptClip || typeof scriptClip.analyzeCut !== 'function') {
            this.logSmoothCuts("skipped", { reason: "analyze_cut_unavailable" });
            return;
        }

        const generationAtStart = this.scanGeneration;

        this.logSmoothCuts("start", {
            occurrenceCount: this.occurrences.length,
            avoidHarshCuts: this.avoidHarshCuts,
            scanGeneration: this.scanGeneration,
        });

        runInAction(() => {
            this.isAnalyzing = true;
            this.analysisProgress = 0;
        });
        
        const total = this.occurrences.length;
        let analyzed = 0;
        
        for (const occurrence of this.occurrences) {
            // Abort if user turned smooth cuts off or tool was closed.
            if (!this.isActive || !this.avoidHarshCuts) {
                this.logSmoothCuts("aborted", {
                    isActive: this.isActive,
                    avoidHarshCuts: this.avoidHarshCuts,
                    analyzed,
                    total,
                });
                break;
            }
            try {
                // Calculate padded cut boundaries
                const pad = occurrence.paddingSec;
                let cutStart = occurrence.startSec - pad;
                let cutEnd = occurrence.endSec + pad;
                
                // Clamp to not exceed neighbor boundaries
                if (occurrence.prevWordEndSec !== null) {
                    cutStart = Math.max(cutStart, occurrence.prevWordEndSec);
                }
                if (occurrence.nextWordStartSec !== null) {
                    cutEnd = Math.min(cutEnd, occurrence.nextWordStartSec);
                }
                
                // Run audio analysis
                const analysis = await scriptClip.analyzeCut(
                    cutStart,
                    cutEnd,
                    occurrence.sourceIndex,
                    occurrence.prevWordEndSec,
                    occurrence.nextWordStartSec
                );
                
                if (analysis) {
                    this.logSmoothCuts("analysis", {
                        id: occurrence.id,
                        word: occurrence.word,
                        categoryId: occurrence.categoryId,
                        sourceIndex: occurrence.sourceIndex,
                        fillerStartSec: occurrence.startSec,
                        fillerEndSec: occurrence.endSec,
                        cutStartSec: cutStart,
                        cutEndSec: cutEnd,
                        isHarsh: analysis.isHarsh,
                        fadeOutMs: analysis.fadeOutMs,
                        fadeInMs: analysis.fadeInMs,
                        rmsRatio: analysis.rmsRatio,
                        score: analysis.score,
                    });
                    // Update harshness based on scoring system
                    runInAction(() => {
                        occurrence.isHarshCut = analysis.isHarsh;
                    });
                } else {
                    this.logSmoothCuts("analysis_unavailable", {
                        id: occurrence.id,
                        word: occurrence.word,
                        categoryId: occurrence.categoryId,
                        sourceIndex: occurrence.sourceIndex,
                        fillerStartSec: occurrence.startSec,
                        fillerEndSec: occurrence.endSec,
                        cutStartSec: cutStart,
                        cutEndSec: cutEnd,
                    });
                }
            } catch (err) {
                this.logSmoothCuts("analysis_error", {
                    id: occurrence.id,
                    word: occurrence.word,
                    categoryId: occurrence.categoryId,
                    sourceIndex: occurrence.sourceIndex,
                    error:
                        err instanceof Error
                            ? { name: err.name, message: err.message, stack: err.stack }
                            : String(err),
                });
            }
            
            analyzed++;
            runInAction(() => {
                this.analysisProgress = Math.round((analyzed / total) * 100);
            });
        }

        runInAction(() => {
            this.isAnalyzing = false;
            this.analysisProgress = 100;
            this.analyzedGeneration = generationAtStart;
        });

        this.logSmoothCuts("complete", {
            analyzed,
            total,
            generationAtStart,
            currentGeneration: this.scanGeneration,
        });

        // If occurrences changed mid-analysis, rerun once for the latest scan.
        if (this.isActive && this.avoidHarshCuts && this.scanGeneration !== generationAtStart) {
            this.maybeAnalyzeForSmoothCuts();
        }
    }
    
    /**
     * Scan transcript for filler words based on current settings.
     */
    scanForFillerWords() {
        void this.refreshDetections();
    }

    private createEmptyCounts() {
        const out = new Map<string, number>();
        for (const category of FILLER_WORD_CATEGORIES) {
            out.set(category.id, 0);
        }
        return out;
    }

    private buildOccurrencesFromLocalCandidates(
        candidates: Array<{
            id: string;
            categoryId: string;
            firstIndex: number;
            lastIndex: number;
            isRepeatedWord: boolean;
            matchedText: string;
        }>,
        counts: Map<string, number>,
    ): FillerWordOccurrence[] {
        const words = this.allWords as FillerWordTokenizedWord[];
        const out: FillerWordOccurrence[] = [];
        for (const candidate of candidates) {
            const occurrence = this.buildOccurrenceFromWordRange({
                id: candidate.id,
                words,
                firstIndex: candidate.firstIndex,
                lastIndex: candidate.lastIndex,
                categoryId: candidate.categoryId,
                isRepeatedWord: candidate.isRepeatedWord,
                wordTextOverride: candidate.matchedText,
            });
            if (!occurrence) continue;
            out.push(occurrence);
            counts.set(candidate.categoryId, (counts.get(candidate.categoryId) || 0) + 1);
        }
        out.sort((a, b) => a.startSec - b.startSec);
        return out;
    }

    private async refreshDetections() {
        const words = this.allWords as FillerWordTokenizedWord[];
        const requestGeneration = ++this.detectionRequestGeneration;
        if (!this.isActive) return;

        const localCandidates = buildLocalFillerWordCandidates(words, this.enabledCategories);
        const provisionalCandidates = localCandidates.filter((candidate) => candidate.decision !== "auto_keep");
        const provisionalCounts = this.createEmptyCounts();
        const provisionalOccurrences = this.buildOccurrencesFromLocalCandidates(
            provisionalCandidates,
            provisionalCounts,
        );

        const ambiguousRequests = buildAmbiguousFillerWordRequests(localCandidates);
        const projectIdSafe = String(ProjectAssetsStore.projectId ?? "").trim();
        const shouldClassifyAmbiguous =
            Boolean(this.postAiFillerWords) &&
            Boolean(projectIdSafe) &&
            ambiguousRequests.length > 0;

        runInAction(() => {
            if (!this.isActive || requestGeneration !== this.detectionRequestGeneration) return;
            this.occurrences = provisionalOccurrences;
            this.categoryCounts = provisionalCounts;
            this.currentIndex = 0;
            this.scanGeneration++;
            this.analyzedGeneration = -1;
            this.isInitializing = shouldClassifyAmbiguous;
        });

        if (!shouldClassifyAmbiguous) {
            const fallbackCandidates = this.postAiFillerWords
                ? localCandidates.filter((candidate) => candidate.decision === "auto_remove")
                : provisionalCandidates;
            const finalCounts = this.createEmptyCounts();
            const finalOccurrences = this.buildOccurrencesFromLocalCandidates(
                fallbackCandidates,
                finalCounts,
            );

            runInAction(() => {
                if (!this.isActive || requestGeneration !== this.detectionRequestGeneration) return;
                this.occurrences = finalOccurrences;
                this.categoryCounts = finalCounts;
                this.currentIndex = 0;
                this.scanGeneration++;
                this.isInitializing = false;
            });
            this.maybeAnalyzeForSmoothCuts();
            return;
        }

        try {
            const decisions = await classifyAmbiguousFillerWords({
                projectId: projectIdSafe,
                candidates: ambiguousRequests,
                postAiFillerWords: this.postAiFillerWords!,
                maxDetections: 3000,
            });

            const finalCandidates =
                decisions.length > 0
                    ? mergeFillerWordServerDecisions(localCandidates, decisions)
                    : provisionalCandidates;
            const finalCounts = this.createEmptyCounts();
            const finalOccurrences = this.buildOccurrencesFromLocalCandidates(
                finalCandidates,
                finalCounts,
            );

            runInAction(() => {
                if (!this.isActive || requestGeneration !== this.detectionRequestGeneration) return;
                this.occurrences = finalOccurrences;
                this.categoryCounts = finalCounts;
                this.currentIndex = 0;
                this.scanGeneration++;
                this.isInitializing = false;
            });
            this.maybeAnalyzeForSmoothCuts();
        } catch (err) {
            console.warn("[FillerWordStore] Ambiguous filler classification failed; keeping provisional local candidates:", err);
            runInAction(() => {
                if (!this.isActive || requestGeneration !== this.detectionRequestGeneration) return;
                this.isInitializing = false;
            });
            this.maybeAnalyzeForSmoothCuts();
        }
    }

    private getPreviousWordInSource(words: V2TranscriptWord[], fromIndex: number, sourceIndex: number): V2TranscriptWord | null {
        for (let i = fromIndex; i >= 0; i--) {
            const w = words[i];
            if ((w?.sourceIndex ?? 0) === sourceIndex) return w;
        }
        return null;
    }

    private getNextWordInSource(words: V2TranscriptWord[], fromIndex: number, sourceIndex: number): V2TranscriptWord | null {
        for (let i = fromIndex; i < words.length; i++) {
            const w = words[i];
            if ((w?.sourceIndex ?? 0) === sourceIndex) return w;
        }
        return null;
    }

    private buildOccurrenceFromWordRange(args: {
        id: string;
        words: V2TranscriptWord[];
        firstIndex: number;
        lastIndex: number;
        categoryId: string;
        isRepeatedWord?: boolean;
        repeatedWith?: string;
        wordTextOverride?: string;
    }): FillerWordOccurrence | null {
        const { words, firstIndex, lastIndex, categoryId, isRepeatedWord, repeatedWith } = args;
        if (firstIndex < 0 || lastIndex < firstIndex || lastIndex >= words.length) return null;

        const matchedWords = words.slice(firstIndex, lastIndex + 1);
        const firstWord = matchedWords[0];
        const lastWord = matchedWords[matchedWords.length - 1];
        if (!firstWord || !lastWord) return null;

        const sourceIndex = firstWord.sourceIndex ?? 0;
        if (matchedWords.some((w) => (w.sourceIndex ?? 0) !== sourceIndex)) {
            return null;
        }

        const fillerStart = Number(firstWord.startSec ?? 0);
        const fillerEnd = Number(lastWord.endSec ?? lastWord.startSec ?? fillerStart);

        const prevWord = this.getPreviousWordInSource(words, firstIndex - 1, sourceIndex);
        const nextWord = this.getNextWordInSource(words, lastIndex + 1, sourceIndex);
        const prevWordEndSec = prevWord?.endSec ?? null;
        const nextWordStartSec = nextWord?.startSec ?? null;
        const prevWordSrcEndSec = prevWord?.srcEndSec ?? prevWord?.endSec ?? null;
        const nextWordSrcStartSec = nextWord?.srcStartSec ?? nextWord?.startSec ?? null;
        const padding = computeFillerPaddingSec(fillerStart, fillerEnd, prevWordEndSec, nextWordStartSec);

        const contextStartTime = fillerStart - 2.0;
        const contextEndTime = fillerEnd + 2.0;
        const contextBefore = words
            .filter((w, idx) => idx < firstIndex && (w.sourceIndex ?? 0) === sourceIndex && (w.startSec ?? 0) >= contextStartTime)
            .map((w) => w.text);
        const contextAfter = words
            .filter((w, idx) => idx > lastIndex && (w.sourceIndex ?? 0) === sourceIndex && (w.startSec ?? 0) <= contextEndTime)
            .map((w) => w.text);

        return {
            id: args.id,
            word: args.wordTextOverride || matchedWords.map((w) => w.text).join(" "),
            categoryId,
            startSec: fillerStart,
            endSec: fillerEnd,
            srcStartSec: firstWord.srcStartSec ?? fillerStart,
            srcEndSec: lastWord.srcEndSec ?? lastWord.endSec ?? fillerEnd,
            sourceIndex,
            contextBefore,
            contextAfter,
            prevWordEndSec,
            nextWordStartSec,
            prevWordSrcEndSec,
            nextWordSrcStartSec,
            isHarshCut: false,
            paddingSec: padding,
            isRepeatedWord: Boolean(isRepeatedWord),
            repeatedWith,
        };
    }

    private scanWithAIDetections(words: FillerWordTokenizedWord[], counts: Map<string, number>): FillerWordOccurrence[] {
        const detections = Array.isArray(this.aiDetections) ? this.aiDetections : [];
        const idxByTokenId = new Map<string, number>();
        for (let i = 0; i < words.length; i++) {
            const tokenId = String((words[i] as any)?.tokenId ?? "");
            if (tokenId) idxByTokenId.set(tokenId, i);
        }

        const out: FillerWordOccurrence[] = [];
        for (const detection of detections) {
            const categoryId =
                this.resolveAICategoryId(String(detection?.categoryId ?? "").trim()) ??
                (detection?.isRepeatedWord ? REPEATED_WORD_CATEGORY_ID : null);
            if (!categoryId) continue;

            const isRepeatedWord = categoryId === REPEATED_WORD_CATEGORY_ID || Boolean(detection?.isRepeatedWord);
            if (!this.enabledCategories.has(categoryId)) continue;

            const startTokenId = String(detection?.startTokenId ?? "").trim();
            const endTokenId = String(detection?.endTokenId ?? "").trim();
            let startIndex = idxByTokenId.get(startTokenId);
            let endIndex = idxByTokenId.get(endTokenId);
            if (startIndex == null || endIndex == null || endIndex < startIndex) {
                const startSec = Number(detection?.startSec);
                const endSec = Number(detection?.endSec);
                const sourceIndex = Number(detection?.sourceIndex);
                if (Number.isFinite(startSec) && Number.isFinite(endSec) && Number.isFinite(sourceIndex)) {
                    const sourceWordIndices = words
                        .map((w, idx) => ({ w, idx }))
                        .filter((row) => Number(row.w?.sourceIndex ?? 0) === sourceIndex);
                    if (sourceWordIndices.length > 0) {
                        const nearestStart = sourceWordIndices.reduce((best, row) => {
                            const d = Math.abs(Number(row.w?.startSec ?? 0) - startSec);
                            return d < best.dist ? { idx: row.idx, dist: d } : best;
                        }, { idx: -1, dist: Number.POSITIVE_INFINITY });
                        const nearestEnd = sourceWordIndices.reduce((best, row) => {
                            const d = Math.abs(Number(row.w?.endSec ?? row.w?.startSec ?? 0) - endSec);
                            return d < best.dist ? { idx: row.idx, dist: d } : best;
                        }, { idx: -1, dist: Number.POSITIVE_INFINITY });
                        if (nearestStart.idx >= 0 && nearestEnd.idx >= nearestStart.idx) {
                            startIndex = nearestStart.idx;
                            endIndex = nearestEnd.idx;
                        }
                    }
                }
            }
            if (startIndex == null || endIndex == null || endIndex < startIndex) continue;

            const occurrence = this.buildOccurrenceFromWordRange({
                id: String(detection?.id ?? `filler_ai_${startTokenId}_${endTokenId}`),
                words,
                firstIndex: startIndex,
                lastIndex: endIndex,
                categoryId,
                isRepeatedWord,
                wordTextOverride: String(detection?.matchedText ?? "").trim() || undefined,
            });
            if (!occurrence) continue;

            out.push(occurrence);
            counts.set(categoryId, (counts.get(categoryId) || 0) + 1);
        }

        return out;
    }

    private resolveAICategoryId(rawCategoryId: string): string | null {
        const raw = String(rawCategoryId ?? "").trim();
        if (!raw) return null;
        if (FILLER_WORD_CATEGORIES.some((c) => c.id === raw)) return raw;

        const normalizeKey = (v: string): string =>
            String(v ?? "")
                .toLowerCase()
                .replace(/[^\w\s-]/g, "")
                .trim()
                .replace(/[\s-]+/g, "_");

        const normalizedRaw = normalizeKey(raw);
        if (!normalizedRaw) return null;

        if (normalizedRaw === REPEATED_WORD_CATEGORY_ID || normalizedRaw === "repeated" || normalizedRaw === "repeated_words") {
            return REPEATED_WORD_CATEGORY_ID;
        }

        for (const category of FILLER_WORD_CATEGORIES) {
            if (normalizeKey(category.id) === normalizedRaw) return category.id;
            if (normalizeKey(category.label) === normalizedRaw) return category.id;
            for (const phrase of category.words) {
                if (normalizeKey(phrase) === normalizedRaw) return category.id;
            }
        }
        return null;
    }

    private scanWithLocalRules(words: V2TranscriptWord[], counts: Map<string, number>): FillerWordOccurrence[] {
        const newOccurrences: FillerWordOccurrence[] = [];

        for (let i = 0; i < words.length; i++) {
            const match = findFillerMatchAt(words, i, this.enabledCategories);
            if (!match) continue;

            const matchEndIndex = i + match.wordCount - 1;
            const occurrence = this.buildOccurrenceFromWordRange({
                id: `filler_${i}_${words[i]?.startSec ?? 0}_${matchEndIndex}`,
                words,
                firstIndex: i,
                lastIndex: matchEndIndex,
                categoryId: match.categoryId,
            });
            if (!occurrence) continue;

            newOccurrences.push(occurrence);
            counts.set(match.categoryId, (counts.get(match.categoryId) || 0) + 1);
            i = matchEndIndex;
        }

        if (this.includeRepeatedWords) {
            const repeats = detectRepeatedWords(words.map((w) => ({
                text: w.text,
                startSec: Number(w.startSec ?? 0),
                endSec: Number(w.endSec ?? w.startSec ?? 0),
            })));

            for (const { firstIndex, secondIndex } of repeats) {
                const occurrence = this.buildOccurrenceFromWordRange({
                    id: `repeat_${secondIndex}_${words[secondIndex]?.startSec ?? 0}`,
                    words,
                    firstIndex: secondIndex,
                    lastIndex: secondIndex,
                    categoryId: REPEATED_WORD_CATEGORY_ID,
                    isRepeatedWord: true,
                    repeatedWith: `word_${firstIndex}`,
                    wordTextOverride: String(words[secondIndex]?.text ?? ""),
                });
                if (!occurrence) continue;
                newOccurrences.push(occurrence);
                counts.set(REPEATED_WORD_CATEGORY_ID, (counts.get(REPEATED_WORD_CATEGORY_ID) || 0) + 1);
            }
        }

        return newOccurrences;
    }

    /**
     * Toggle a filler word category.
     */
    toggleCategory(categoryId: string) {
        if (this.enabledCategories.has(categoryId)) {
            this.enabledCategories.delete(categoryId);
        } else {
            this.enabledCategories.add(categoryId);
        }
        void this.refreshDetections();
    }
    
    /**
     * Select all categories.
     */
    selectAllCategories() {
        this.enabledCategories = new Set(FILLER_WORD_CATEGORIES.map(c => c.id));
        void this.refreshDetections();
    }
    
    /**
     * Deselect all categories.
     */
    deselectAllCategories() {
        this.enabledCategories.clear();
        void this.refreshDetections();
    }
    
    /**
     * Toggle repeated words inclusion.
     */
    toggleRepeatedWords() {
        if (this.includeRepeatedWords) {
            this.enabledCategories.delete(REPEATED_WORD_CATEGORY_ID);
        } else {
            this.enabledCategories.add(REPEATED_WORD_CATEGORY_ID);
        }
        void this.refreshDetections();
    }

    /**
     * Toggle avoid harsh cuts.
     * When turning ON, triggers audio analysis if not already done.
     */
    toggleAvoidHarshCuts() {
        this.avoidHarshCuts = !this.avoidHarshCuts;
        // Reset index since filtered list changed
        this.currentIndex = 0;
        
        // If turning on and we haven't analyzed yet, run analysis
        if (this.avoidHarshCuts && !this.isAnalyzing) {
            this.maybeAnalyzeForSmoothCuts();
        }
    }

    /**
     * Set the cut mode.
     */
    setCutMode(mode: FillerWordCutMode) {
        this.cutMode = mode;
    }

    async revealOccurrenceInTranscript(occurrence: FillerWordOccurrence | null) {
        if (!occurrence) return;

        const startSec = Number(occurrence.startSec ?? 0);
        const endSec = Number(occurrence.endSec ?? startSec);
        const epsilon = 1e-3;
        const focusSrcTime =
            Number.isFinite(endSec) && endSec > startSec + epsilon
                ? Math.min(endSec - epsilon, startSec + epsilon)
                : startSec;

        const mapped = ScriptLayerStore.mapSourceTimeToCompositionTime(focusSrcTime);
        const playbackRate = Number(ScriptLayerStore.playbackRate || 1) || 1;
        const seekTime = mapped != null ? mapped : focusSrcTime / playbackRate;

        try {
            await CompositionService.seek(seekTime);
        } finally {
            focusTranscriptWordAtTime(focusSrcTime);
        }
    }

    /**
     * Navigate to next occurrence.
     */
    goToNext() {
        if (this.filteredOccurrences.length === 0) return;
        this.currentIndex = (this.currentIndex + 1) % this.filteredOccurrences.length;
    }

    /**
     * Navigate to previous occurrence.
     */
    goToPrevious() {
        if (this.filteredOccurrences.length === 0) return;
        this.currentIndex = (this.currentIndex - 1 + this.filteredOccurrences.length) % this.filteredOccurrences.length;
    }

    /**
     * Go to a specific occurrence by index.
     */
    goToIndex(index: number) {
        if (index >= 0 && index < this.filteredOccurrences.length) {
            this.currentIndex = index;
        }
    }

    /**
     * Get the current occurrence.
     */
    get currentOccurrence(): FillerWordOccurrence | null {
        return this.filteredOccurrences[this.currentIndex] ?? null;
    }
    
    /**
     * Get total count of ALL occurrences (including harsh).
     */
    get totalCount(): number {
        return this.occurrences.length;
    }
    
    /**
     * Get count of harsh cuts.
     */
    get harshCount(): number {
        return this.occurrences.filter(o => o.isHarshCut).length;
    }
    
    /**
     * Get count of non-harsh (safe) cuts.
     */
    get safeCount(): number {
        return this.occurrences.filter(o => !o.isHarshCut).length;
    }
    
    /**
     * Get count of removable occurrences (based on avoidHarshCuts toggle).
     */
    get removableCount(): number {
        if (this.avoidHarshCuts) {
            return this.safeCount;
        }
        return this.totalCount;
    }
    
    /**
     * Get filtered occurrences based on avoidHarshCuts setting.
     * This is what should be used for display and removal.
     */
    get filteredOccurrences(): FillerWordOccurrence[] {
        if (this.avoidHarshCuts) {
            return this.occurrences.filter(o => !o.isHarshCut);
        }
        return this.occurrences;
    }
    
    /**
     * Check if a word at a given time is in the filler word occurrences.
     * Returns the occurrence if found, null otherwise.
     * Respects avoidHarshCuts setting - only returns non-harsh occurrences when enabled.
     */
    getOccurrenceAtTime(startSec: number): FillerWordOccurrence | null {
        // Use a small epsilon for floating point comparison
        const epsilon = 0.01;
        // Search in filteredOccurrences to respect avoidHarshCuts setting
        return this.filteredOccurrences.find(o => 
            startSec >= (o.startSec - epsilon) && startSec <= (o.endSec + epsilon)
        ) ?? null;
    }
    
    /**
     * Check if a word at a given time is the currently selected filler word.
     * Respects avoidHarshCuts - won't match if current occurrence is harsh and avoid is enabled.
     */
    isCurrentOccurrence(startSec: number): boolean {
        const current = this.currentOccurrence;
        if (!current) return false;
        // If avoidHarshCuts is enabled and current is harsh, don't highlight
        if (this.avoidHarshCuts && current.isHarshCut) return false;
        const epsilon = 0.01;
        return startSec >= (current.startSec - epsilon) && startSec <= (current.endSec + epsilon);
    }
    
    /**
     * Get context words with timestamps for playback preview.
     * Returns words within 2 seconds before and 2 seconds after the filler word.
     */
    getContextWordsWithTimestamps(occurrence: FillerWordOccurrence): {
        wordsBefore: Array<{ text: string; startSec: number; endSec: number }>;
        fillerWord: { text: string; startSec: number; endSec: number };
        wordsAfter: Array<{ text: string; startSec: number; endSec: number }>;
    } | null {
        const epsilon = 0.01;
        const CONTEXT_SECONDS = 2.0; // 2 seconds before and after
        
        const fillerIndex = this.allWords.findIndex(w => 
            Math.abs(w.startSec - occurrence.startSec) < epsilon
        );
        
        if (fillerIndex === -1) return null;
        
        const fillerWord = {
            text: occurrence.word,
            startSec: occurrence.startSec,
            endSec: occurrence.endSec ?? occurrence.startSec,
        };
        
        // Get words within 2 seconds BEFORE the filler word starts
        const contextStartTime = occurrence.startSec - CONTEXT_SECONDS;
        const wordsBefore = this.allWords
            .filter((w, idx) => idx < fillerIndex && w.startSec >= contextStartTime)
            .map(w => ({
                text: w.text,
                startSec: w.startSec,
                endSec: w.endSec ?? w.startSec,
            }));
        
        // Get words within 2 seconds AFTER the filler word ends
        const fillerEndSec = occurrence.endSec ?? occurrence.startSec;
        const contextEndTime = fillerEndSec + CONTEXT_SECONDS;
        const wordsAfter = this.allWords
            .filter((w, idx) => idx > fillerIndex && w.startSec > fillerEndSec + epsilon && w.startSec <= contextEndTime)
            .map(w => ({
                text: w.text,
                startSec: w.startSec,
                endSec: w.endSec ?? w.startSec,
            }));
        
        return { wordsBefore, fillerWord, wordsAfter };
    }
    
    /**
     * Get category info with counts.
     */
    get categoriesWithCounts(): Array<{ id: string; label: string; count: number; enabled: boolean }> {
        return FILLER_WORD_CATEGORIES.map(category => ({
            id: category.id,
            label: category.label,
            count: this.categoryCounts.get(category.id) || 0,
            enabled: this.enabledCategories.has(category.id),
        }));
    }
    
    /**
     * Check if all categories are selected.
     */
    get allCategoriesSelected(): boolean {
        return this.enabledCategories.size === FILLER_WORD_CATEGORIES.length;
    }
    
    /**
     * Check if some (but not all) categories are selected.
     */
    get someCategoriesSelected(): boolean {
        return this.enabledCategories.size > 0 && 
               this.enabledCategories.size < FILLER_WORD_CATEGORIES.length;
    }
    
    /**
     * Remove current occurrence (applies cut).
     * Returns the occurrence that was removed.
     */
    removeCurrent(): FillerWordOccurrence | null {
        const current = this.currentOccurrence;
        if (!current) return null;
        
        // Remove from occurrences list
        this.occurrences = this.occurrences.filter(o => o.id !== current.id);
        if (Array.isArray(this.aiDetections)) {
            this.aiDetections = this.aiDetections.filter((d) => String(d.id) !== String(current.id));
        }
        
        // Also remove affected words from allWords to keep context accurate.
        const epsilon = 0.01;
        this.allWords = this.allWords.filter(w => 
            !(
                (w.sourceIndex ?? 0) === current.sourceIndex &&
                (w.startSec ?? 0) >= (current.startSec - epsilon) &&
                (w.endSec ?? w.startSec ?? 0) <= (current.endSec + epsilon)
            )
        );
        
        // Update category counts
        const categoryId = current.categoryId;
        if (categoryId && this.categoryCounts.has(categoryId)) {
            const count = this.categoryCounts.get(categoryId) || 0;
            this.categoryCounts.set(categoryId, Math.max(0, count - 1));
        }
        
        // Adjust index if needed
        if (this.currentIndex >= this.filteredOccurrences.length) {
            this.currentIndex = Math.max(0, this.filteredOccurrences.length - 1);
        }
        
        return current;
    }
    
    /**
     * Get all occurrences for removal (respects avoidHarshCuts).
     */
    getAllForRemoval(): FillerWordOccurrence[] {
        return [...this.filteredOccurrences];
    }
    
    /**
     * Clear all occurrences (after remove all).
     */
    clearAll() {
        this.occurrences = [];
        this.currentIndex = 0;
    }
}

export const FillerWordStore = new FillerWordStoreImpl();
